import './SystemAnalytics.css';
import type React from 'react';
import { Container, Row, Col, Button, Spinner } from 'react-bootstrap';
import MetricCard from '../../components/SystemAnalytics/MetricCard';
import systemAnalyticsIcon from '../../assets/icon/systemAnalyticsIcon.svg';
import requestIcon from '../../assets/icon/requestIcon.svg';
import userIcon from '../../assets/icon/userIcon.svg';
import rpsIcon from '../../assets/icon/rpsIcon.svg';
import rateIcon from '../../assets/icon/rateIcon.svg';
import cprIcon from '../../assets/icon/cprIcon.svg';
import filterIcon from '../../assets/icon/filterIcon.svg';
import calenderIcon from '../../assets/icon/calenderIcon.svg';
import { ReactComponent as ExportIcon } from '../../assets/icon/Export.svg';
import { useEffect, useRef, useState } from 'react';
import FilterForm from '../../components/SystemAnalytics/FilterForm';
import {
  fetchSystemAnalyticsData,
  filterDropdownData
  // filterDropdownDataCloudProvider
} from '../../service/apiServices';
import { useAzureData } from '../../context/AzureContext';
import LineChart from '../../components/AnalyticsChart/LineChart';
import DonutChart from '../../components/AnalyticsChart/DonutChart';
import HorizontalBarChart from '../../components/AnalyticsChart/HorizontalBarChart';
import MultiBarChart from '../../components/AnalyticsChart/MultiBarChart';
import { Info } from 'lucide-react';
import { ToolTipFunction } from '../../components/inputField';
import useCloudProvider from '../../hooks/useCloudProvider';
import UnileverAnimation from '../../components/UnileverAnimation';
import { analyticsSheetBuilders, exportAnalyticsReport } from '../../utilities/analyticsExport';

const getDefaultDateRange = () => {
  const today = new Date();
  const pastDate = new Date();
  // pastDate.setMonth(today.getMonth() - 3); // 3 months before today
  pastDate.setDate(today.getDate() - 6); // 7 days before today
  return { fromDate: pastDate, toDate: today };
};

const truncateText = (text: string, maxLength = 30) => {
  if (!text) return '';
  return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
};

interface CardsData {
  metricsData: {
    title: string;
    value: string;
    subtitle: string;
  }[];
}

interface DropdownValues {
  filters: {
    applications: string[];
    environments: string[];
    model_names: string[];
  };
}

const SystemAnalytics: React.FC = () => {
  const { apiToken } = useAzureData();
  const [show, setShow] = useState(false);
  const { fromDate, toDate } = getDefaultDateRange();
  const [dateRange, setDateRange] = useState({
    from: fromDate?.toLocaleDateString('en-GB'),
    to: toDate?.toLocaleDateString('en-GB')
  });
  const [systemAnalyticsData, setSystemAnalyticsData] = useState<any>(null);
  const [cardsData, setCardsData] = useState<CardsData | null>(null);
  const [filterDropdownValues, setFilterDropdownvalues] = useState<DropdownValues | null>(null);
  const [loading, setLoading] = useState(false);
  const [dropdownValuesLoading, setDropdownValuesLoading] = useState(false);
  const [choosedFilter, setChoosedFilter] = useState<any>('');
  const [currentchooseApp, setCurrentChoosedApp] = useState<any>('');
  const [showOnlyTimePeriodFilter, setShowOnlyTimePeriodFilter] = useState(false);
  const hasFetchedRef = useRef(false);
  const { selectedCloud, cloudProvidersList, selectCloud } = useCloudProvider();
  const [cloudFilterID, setCloudFilter] = useState(0);
  const fetchData = async (appliedFilters: any) => {
    setLoading(true);
    try {
      let response = await fetchSystemAnalyticsData(apiToken, appliedFilters);
      response && setSystemAnalyticsData(response?.data);
      response && setCardsData(response?.data);
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // console.log(
  //   systemAnalyticsData?.top_models_token_counts,
  //   'fdsfdfsdd',
  //   systemAnalyticsData?.topApplicationsBytokens
  // );

  const fetchDropdownData = async () => {
    setDropdownValuesLoading(true);
    try {
      let response = await filterDropdownData(
        apiToken,
        currentchooseApp ? currentchooseApp : '',
        cloudFilterID ? cloudFilterID : 0
      );
      response && setFilterDropdownvalues(response);
      setDropdownValuesLoading(false);
    } catch (error) {
      setDropdownValuesLoading(false);
      console.error('Error:', error);
    }
  };

  useEffect(() => {
    fetchDropdownData();
  }, [cloudFilterID]);

  useEffect(() => {
    if (hasFetchedRef?.current) return;
    hasFetchedRef.current = true;
    dateRange?.to && fetchData({});
    // fetchCardData()
    fetchDropdownData();
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchDropdownData();
    //eslint-disable-next-line
  }, [currentchooseApp]);

  const metricsData = [
    {
      title: cardsData && cardsData?.metricsData[0]?.title,
      value: cardsData && cardsData?.metricsData[0]?.value,
      subtitle: cardsData && cardsData?.metricsData[0]?.subtitle,
      icon: requestIcon
    },
    {
      title: cardsData && cardsData?.metricsData[1]?.title,
      value: cardsData && cardsData?.metricsData[1]?.value,
      subtitle: cardsData && cardsData?.metricsData[1]?.subtitle,
      icon: userIcon
    },
    {
      title: cardsData && cardsData?.metricsData[2]?.title,
      value: cardsData && cardsData?.metricsData[2]?.value,
      subtitle: cardsData && cardsData?.metricsData[2]?.subtitle,
      icon: rpsIcon
    },
    {
      title: cardsData && cardsData?.metricsData[3]?.title,
      value: cardsData && cardsData?.metricsData[3]?.value,
      subtitle: cardsData && cardsData?.metricsData[3]?.subtitle,
      icon: rateIcon
    },
    {
      title: cardsData && cardsData?.metricsData[4]?.title,
      value: cardsData && cardsData?.metricsData[4]?.value,
      subtitle: cardsData && cardsData?.metricsData[4]?.subtitle,
      icon: cprIcon
    }
  ];

  let applicationNames =
    filterDropdownValues &&
    filterDropdownValues?.filters?.applications?.length > 0 &&
    filterDropdownValues?.filters?.applications?.map((item) => ({ label: item, value: item }));
  let modelNames =
    filterDropdownValues &&
    filterDropdownValues?.filters?.model_names?.length > 0 &&
    filterDropdownValues?.filters?.model_names?.map((item) => ({ label: item, value: item }));

  const [selectedToggleButton, setSelectedToggleButton] = useState(0);
  const selectedCloudName =
    cloudProvidersList.find((item) => item.id === selectedCloud)?.name || 'All Cloud Providers';

  const handleExport = () => {
    exportAnalyticsReport({
      fileName: `system_analytics_${choosedFilter?.application || 'all_apps'}_${
        choosedFilter?.modelName || 'all_models'
      }`,
      sheets: [
        {
          name: 'Filters',
          rows: [
            { Field: 'Application', Value: choosedFilter?.application || 'All Applications' },
            { Field: 'Model', Value: choosedFilter?.modelName || 'All Models' },
            { Field: 'Cloud Provider', Value: selectedCloudName },
            { Field: 'Date From', Value: choosedFilter?.timePeriod?.from || dateRange.from },
            { Field: 'Date To', Value: choosedFilter?.timePeriod?.to || dateRange.to }
          ]
        },
        {
          name: 'KPI Cards',
          rows: metricsData?.map((metric) => ({
            Title: metric.title,
            Value: metric.value,
            Subtitle: metric.subtitle
          }))
        },
        { name: 'Avg Response Time', rows: analyticsSheetBuilders.toSeriesRows(systemAnalyticsData?.avgResponseTime) },
        { name: 'API Volume', rows: analyticsSheetBuilders.toSeriesRows(systemAnalyticsData?.APIVolumeChart) },
        { name: 'Tokens Chart', rows: analyticsSheetBuilders.toSeriesRows(systemAnalyticsData?.tokensChart) },
        {
          name: 'Request Outcome',
          rows: analyticsSheetBuilders.toSeriesRows(systemAnalyticsData?.requestOutcomeDonut)
        },
        {
          name: 'API Health',
          rows: analyticsSheetBuilders.toSeriesRows(systemAnalyticsData?.ApiHealthChart)
        },
        { name: 'Top Models by Requests', rows: analyticsSheetBuilders.toSeriesRows(systemAnalyticsData?.topModels) },
        { name: 'Top Models by Tokens', rows: analyticsSheetBuilders.toSeriesRows(systemAnalyticsData?.top_models_token_counts) },
        {
          name: 'Top Applications by Requests',
          rows: analyticsSheetBuilders.toSeriesRows(systemAnalyticsData?.topApplications)
        },
        {
          name: 'Top Applications by Tokens',
          rows: analyticsSheetBuilders.toSeriesRows(systemAnalyticsData?.topApplicationsBytokens)
        }
      ].filter((sheet) => sheet.rows && sheet.rows.length > 0)
    });
  };

  return (
    <>
      <Container fluid className="p-4 systemAnalytics_container">
        {/* Header */}
        <div className="mb-2">
          <Row className="justify-content-between align-items-start">
            <Col>
              <h2 className="analytics_heading m-0">
                <img src={systemAnalyticsIcon} alt="analytics" /> System Analytics
              </h2>
              <p className="analytics_subheading">
                View real-time system status, latency metrics, uptime, and throughput to ensure
                reliable AI delivery across your applications.
              </p>
            </Col>
          </Row>
        </div>
        {loading ? (
          <div className="d-flex justify-content-center" style={{ marginTop: '10%' }}>
            {/* <Spinner animation="border" variant="primary" /> */}
            <UnileverAnimation />
          </div>
        ) : (
          <>
            {/* Filter section */}
            <Row className="d-flex mb-4">
              <Col className="d-flex align-align-items-center">
                <div className="d-flex align-items-center show_date_btn me-2">
                  {truncateText(choosedFilter?.application) || 'All Applications'}
                </div>
                <div className="d-flex align-items-center show_date_btn me-2">
                  {truncateText(choosedFilter?.modelName) || 'All Models'}
                </div>

                {
                  <div className="d-flex align-items-center show_date_btn me-2">
                    {['Azure', 'GCP']?.[cloudFilterID - 1] || 'GCP and Azure'}
                  </div>
                }
                {choosedFilter && (
                  <div className="d-flex align-items-center gap-2 show_date_btn">
                    <img src={calenderIcon} alt="calender" />
                    {dateRange.from} - {dateRange.to}
                  </div>
                )}
              </Col>
              <Col className="d-flex justify-content-end">
                <ToolTipFunction
                  toolTipText={
                    <>
                      <p>
                        <b>Unless a filter is applied, the data shown will be as follows:</b>
                      </p>
                      <ul>
                        <li>
                          <b>Cards</b> - Cumulative data up to the current date
                        </li>
                        <li>
                          <b>Graphs</b> - Data from the last 7 days
                        </li>
                      </ul>
                    </>
                  }>
                  <Info />
                </ToolTipFunction>
                <Button
                  onClick={() => setShow(true)}
                  variant="outline-secondary"
                  className="d-flex align-items-center ms-2 gap-2 filterBtn">
                  <img src={filterIcon} alt="filter" />
                  Filter
                </Button>
                <Button
                  onClick={handleExport}
                  variant="outline-secondary"
                  className="d-flex align-items-center ms-2 gap-2 filterBtn"
                  disabled={loading || !systemAnalyticsData}>
                  <ExportIcon width={16} height={16} />
                  Export
                </Button>
              </Col>
            </Row>
            {/* Metrics Cards */}
            <Row className="g-3 d-flex flex-wrap mb-4">
              {metricsData.map((metric, index) => (
                <Col className="" style={{ minWidth: '250px' }} key={index}>
                  <MetricCard
                    title={metric.title}
                    value={metric.value}
                    subtitle={metric.subtitle}
                    icon={metric.icon}
                  />
                </Col>
              ))}
            </Row>
            <Row className="mb-4" style={{ width: '250px' }}>
              <Col>
                <div className="d-flex align-items-center gap-2 show_date_btn">
                  <img src={calenderIcon} alt="calender" />
                  {dateRange.from} - {dateRange.to}
                </div>
              </Col>
            </Row>
            {/* Charts Row 1 */}
            <Row className="g-4 mb-4">
              <Col xs={12} lg={6}>
                {/*  @ts-ignore */}
                <LineChart
                  LineChartData={systemAnalyticsData && systemAnalyticsData?.avgResponseTime}
                  showLegend={false}
                  showPercentYaxis={false}
                />
              </Col>
              <Col xs={12} lg={6}>
                {/* @ts-ignore */}
                <MultiBarChart
                  MultiBarChartData={systemAnalyticsData && systemAnalyticsData?.APIVolumeChart}
                />
              </Col>
            </Row>
            {/* Charts Row 3 */}
            <Row className="g-4 mb-4">
              <Col xs={12} lg={6}>
                {/*  @ts-ignore */}
                <LineChart
                  LineChartData={systemAnalyticsData?.tokensChart}
                  showLegend={false}
                  showPercentYaxis={false}
                />
              </Col>
              <Col xs={12} lg={6}>
                {/*  @ts-ignore */}
                <DonutChart
                  DonutChartData={systemAnalyticsData && systemAnalyticsData?.requestOutcomeDonut}
                  title="Request Breakdown by Outcome"
                  subtitle="Breakdown of all the successfully and erroneous requests"
                />
              </Col>
            </Row>
            {/* systemAnalyticsData?.top_models_token_counts, 'fdsfdfsdd',
            systemAnalyticsData?.topApplicationsBytokens */}
            {/* Charts Row 2 */}
            <Row className="g-4 mb-4">
              <Col xs={12} lg={6}>
                {/*  @ts-ignore */}
                <HorizontalBarChart
                  selectedButtonIndex={selectedToggleButton}
                  buttonNames={['By Requests', 'By Token']}
                  handleClick={() => {
                    setSelectedToggleButton((prev) => (prev === 0 ? 1 : 0));
                  }}
                  HorizontalBarChartData={
                    [
                      systemAnalyticsData && systemAnalyticsData?.topModels,
                      systemAnalyticsData && systemAnalyticsData?.top_models_token_counts
                    ][selectedToggleButton]
                  }
                />
              </Col>
              <Col xs={12} lg={6}>
                {/*  @ts-ignore */}
                <HorizontalBarChart
                  selectedButtonIndex={selectedToggleButton}
                  buttonNames={['By Requests', 'By Token']}
                  handleClick={() => {
                    setSelectedToggleButton((prev) => (prev === 0 ? 1 : 0));
                  }}
                  HorizontalBarChartData={
                    [
                      systemAnalyticsData && systemAnalyticsData?.topApplications,
                      systemAnalyticsData && systemAnalyticsData?.topApplicationsBytokens
                    ][selectedToggleButton]
                  }
                />
              </Col>
            </Row>
          </>
        )}
      </Container>

      <FilterForm
        show={show}
        onClose={() => {
          setShow(false);
          setShowOnlyTimePeriodFilter(false);
        }}
        onApply={(filters) => {
          fetchData(filters);
          setChoosedFilter(filters);
          setCloudFilter(selectedCloud);
        }}
        onDateRangeChange={setDateRange}
        onCurrentChoosedAppChange={setCurrentChoosedApp}
        dropdownValuesLoading={dropdownValuesLoading}
        showCloudProvider={true}
        selectedCloud={selectedCloud}
        cloudProvidersList={cloudProvidersList}
        selectCloud={selectCloud}
        fields={
          showOnlyTimePeriodFilter
            ? [{ key: 'timePeriod', label: 'Time Period', type: 'dateRange' }]
            : [
                { key: 'timePeriod', label: 'Time Period', type: 'dateRange' },
                {
                  key: 'application',
                  label: 'Application',
                  type: 'select',
                  options: applicationNames ? applicationNames : []
                },
                {
                  key: 'modelName',
                  label: 'Model Name',
                  type: 'select',
                  options: modelNames ? modelNames : []
                }
              ]
        }
      />
    </>
  );
};

export default SystemAnalytics;
